<template>
	<!-- Receipt header/footer fields (spec §7 PosSettings card 3; reused by the
	     event override tab) with a live 80 mm preview on the right. -->
	<div class="row">
		<div class="col-lg-7">
			<div class="form-group">
				<label class="form-control-label d-block">{{ $t("pos.settings.logo") }}</label>
				<div class="d-flex align-items-center">
					<img v-if="value.logo_data_url" :src="value.logo_data_url" class="pos-logo-thumb mr-3" alt="" />
					<label class="btn btn-sm btn-outline-primary mb-0">
						{{ $t("pos.settings.logo_upload") }}
						<input type="file" accept="image/png,image/jpeg" class="d-none" @change="onLogo" />
					</label>
					<base-button v-if="value.logo_data_url" size="sm" type="link" @click="set('logo_data_url', '')">{{ $t("pos.settings.logo_remove") }}</base-button>
				</div>
				<small class="text-muted">{{ $t("pos.settings.logo_hint") }}</small>
			</div>
			<base-input :label="$t('pos.settings.shop_name')" :value="value.shop_name" maxlength="150" @input="(v) => set('shop_name', v)"></base-input>
			<div class="form-group">
				<label class="form-control-label">{{ $t("pos.settings.address") }}</label>
				<el-input type="textarea" :rows="2" :value="value.address" maxlength="500" @input="(v) => set('address', v)"></el-input>
			</div>
			<base-input :label="$t('pos.settings.phone')" :value="value.phone" maxlength="50" @input="(v) => set('phone', v)"></base-input>
			<base-input :label="$t('pos.settings.header_note')" :value="value.header_note" maxlength="255" @input="(v) => set('header_note', v)"></base-input>
			<div class="form-group">
				<label class="form-control-label">{{ $t("pos.settings.footer_text") }}</label>
				<el-input type="textarea" :rows="2" :value="value.footer_text" maxlength="1000" @input="(v) => set('footer_text', v)"></el-input>
			</div>
			<base-input :label="$t('pos.settings.footer_qr_text')" :value="value.footer_qr_text" maxlength="500" @input="(v) => set('footer_qr_text', v)"></base-input>
			<base-input :label="$t('pos.settings.footer_qr_caption')" :value="value.footer_qr_caption" maxlength="100" @input="(v) => set('footer_qr_caption', v)"></base-input>
			<el-checkbox :value="value.show_customer_address !== false" @input="(v) => set('show_customer_address', v)">{{ $t("pos.settings.show_customer_address") }}</el-checkbox>
		</div>
		<div class="col-lg-5">
			<label class="form-control-label d-block">{{ $t("pos.settings.preview") }}</label>
			<pos-receipt-preview :model="sample"></pos-receipt-preview>
		</div>
	</div>
</template>

<script>
import { Checkbox } from "element-ui";
import swal from "sweetalert2";
import PosReceiptPreview from "./PosReceiptPreview.vue";
import { posSampleReceiptModel, posLogoOk } from "@/utils/pos";

const LOGO_MAX_WIDTH = 384;

export default {
	name: "PosReceiptForm",
	components: { PosReceiptPreview, [Checkbox.name]: Checkbox },
	props: { value: { type: Object, required: true } },
	computed: {
		sample() {
			return posSampleReceiptModel(this.value);
		}
	},
	methods: {
		set(k, v) {
			this.$emit("input", { ...this.value, [k]: v });
		},
		// Client resize to ≤ 384 px wide + grayscale, PNG data URL ≤ 200 KB (R18).
		onLogo(e) {
			const file = e.target.files && e.target.files[0];
			e.target.value = "";
			if (!file) return;
			const reader = new FileReader();
			reader.onload = () => {
				const img = new Image();
				img.onload = () => {
					const scale = Math.min(1, LOGO_MAX_WIDTH / img.width);
					const w = Math.max(1, Math.round(img.width * scale));
					const h = Math.max(1, Math.round(img.height * scale));
					const canvas = document.createElement("canvas");
					canvas.width = w;
					canvas.height = h;
					const ctx = canvas.getContext("2d");
					ctx.fillStyle = "#fff";
					ctx.fillRect(0, 0, w, h);
					ctx.drawImage(img, 0, 0, w, h);
					const px = ctx.getImageData(0, 0, w, h);
					for (let i = 0; i < px.data.length; i += 4) {
						const g = Math.round(0.299 * px.data[i] + 0.587 * px.data[i + 1] + 0.114 * px.data[i + 2]);
						px.data[i] = px.data[i + 1] = px.data[i + 2] = g;
					}
					ctx.putImageData(px, 0, 0);
					let url = canvas.toDataURL("image/png");
					if (!posLogoOk(url)) url = canvas.toDataURL("image/jpeg", 0.8);
					if (!posLogoOk(url)) {
						swal.fire({ title: this.$t("pos.settings.logo_too_big"), icon: "warning" });
						return;
					}
					this.set("logo_data_url", url);
				};
				img.src = reader.result;
			};
			reader.readAsDataURL(file);
		}
	}
};
</script>

<style>
.pos-logo-thumb {
	max-height: 48px;
	max-width: 160px;
	border: 1px solid #e9ecef;
	filter: grayscale(1);
}
</style>
